import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Container } from './Container';
import { Reveal } from './Reveal';
import styles from './Section.module.css';

export interface SectionProps {
  /** Anchor target — must match the id used in the nav. */
  id: string;
  eyebrow?: string;
  title?: ReactNode;
  lead?: string;
  /** `raised` lifts the band off the page background to separate sections. */
  tone?: 'default' | 'raised';
  align?: 'start' | 'center';
  narrow?: boolean;
  /** Id of an external heading, for sections that lay out their own header. */
  labelledBy?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Vertical rhythm, band styling and the standard section heading in one place,
 * so every section on the page lines up without repeating layout CSS.
 */
export function Section({
  id,
  eyebrow,
  title,
  lead,
  tone = 'default',
  align = 'start',
  narrow = false,
  labelledBy,
  className,
  children,
}: SectionProps) {
  const labelId = `${id}-title`;
  const hasHeading = Boolean(title);

  return (
    <section
      id={id}
      aria-labelledby={hasHeading ? labelId : labelledBy}
      className={cx(styles.section, tone === 'raised' && styles.raised, className)}
    >
      <Container narrow={narrow}>
        {hasHeading && (
          <Reveal as="header" className={cx(styles.header, align === 'center' && styles.centered)}>
            {eyebrow && <span className={styles.eyebrow}>{eyebrow}</span>}
            <h2 id={labelId} className={styles.title}>
              {title}
            </h2>
            {lead && <p className={styles.lead}>{lead}</p>}
          </Reveal>
        )}
        {children}
      </Container>
    </section>
  );
}
